// Undo / redo history for the builder. Rapid edits with the same tag (e.g. typing one label) collapse into one step.
import { useCallback, useEffect, useRef, useState } from 'react';

export default function useHistory(initial = null, cap = 120) {
  const [present, setPresent] = useState(initial);
  const past = useRef([]);
  const future = useRef([]);
  const last = useRef({ tag: null, at: 0 });
  const [, tick] = useState(0);
  const bump = () => tick((n) => n + 1);

  const reset = useCallback((value) => { past.current = []; future.current = []; last.current = { tag: null, at: 0 }; setPresent(value); bump(); }, []);

  const set = useCallback((fn, tag = null) => {
    setPresent((cur) => {
      const next = JSON.parse(JSON.stringify(cur));
      fn(next);
      const now = Date.now();
      const merge = tag && tag === last.current.tag && now - last.current.at < 1200;
      if (!merge) { past.current.push(cur); if (past.current.length > cap) past.current.shift(); }
      future.current = [];
      last.current = { tag, at: now };
      return next;
    });
    bump();
  }, [cap]);

  // Replace the present without touching history (e.g. server response after save).
  const replace = useCallback((fn) => { setPresent((cur) => (typeof fn === 'function' ? fn(cur) : fn)); }, []);

  const undo = useCallback(() => { setPresent((cur) => { if (!past.current.length) return cur; future.current.push(cur); const p = past.current.pop(); last.current = { tag: null, at: 0 }; return p; }); bump(); }, []);
  const redo = useCallback(() => { setPresent((cur) => { if (!future.current.length) return cur; past.current.push(cur); const n = future.current.pop(); last.current = { tag: null, at: 0 }; return n; }); bump(); }, []);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target?.isContentEditable) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      if (e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  return { state: present, set, replace, reset, undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0 };
}
